import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Heart, DollarSign, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader 
} from '@/components/layout/MobileLayout';
import { mockEvents } from '@/lib/mockData';

const tipAmounts = [5, 10, 25, 50, 100];

export function UserTipArtist() {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');

  const event = mockEvents[0];
  const artist = event.artists[0];

  const finalAmount = selectedAmount || (customAmount ? parseFloat(customAmount) : 0);

  return (
    <div className="min-h-screen bg-background" data-testid="user-tip-artist">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <DesktopHeader />
      
      <main className="pt-14 lg:pt-20 pb-24 lg:pb-12">
        <div className="container mx-auto px-4 max-w-md">
          {/* Back button */}
          <Link to={`/events/${event.id}/watch`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Stream
          </Link>

          <Card className="bg-card border-border overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary/20 to-accent/20 p-6 text-center">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden border-4 border-primary">
                <img 
                  src={artist.image} 
                  alt={artist.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h2 className="text-xl font-bold text-white">{artist.name}</h2>
              <p className="text-muted-foreground">Performing at {event.title}</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Tip message */}
              <div className="text-center">
                <Heart className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-white">Show Your Support</h3>
                <p className="text-sm text-muted-foreground">
                  Your tip goes directly to the artist
                </p>
              </div>

              {/* Quick tip amounts */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Select Amount</label>
                <div className="grid grid-cols-5 gap-2">
                  {tipAmounts.map(amount => (
                    <button
                      key={amount}
                      className={`py-3 rounded-lg font-semibold transition-all ${
                        selectedAmount === amount 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-secondary text-white hover:bg-secondary/80'
                      }`}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount('');
                      }}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom amount */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Or Enter Custom Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    className="bg-secondary border-border pl-9"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Add a Message (Optional)</label>
                <textarea
                  placeholder="Say something nice..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none h-24"
                />
              </div>

              {/* Total */}
              {finalAmount > 0 && (
                <Card className="bg-secondary/50 border-border p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Tip Amount</span>
                    <span className="text-2xl font-bold text-accent">${finalAmount.toFixed(2)}</span>
                  </div>
                </Card>
              )}

              {/* Send Tip Button */}
              <Button 
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 text-lg font-semibold"
                disabled={finalAmount <= 0}
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Send Tip
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Tips are processed securely. A small processing fee may apply.
              </p>
            </div>
          </Card>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

export default UserTipArtist;
